import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Database } from '@/integrations/supabase/types';

type ProfileInsert = Database['public']['Tables']['profiles']['Insert'];

export default function AuthCallback() {
  const navigate = useNavigate();

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        console.log('Starting auth callback...');
        
        // Get the session after email verification
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Error getting session:', error);
          throw error;
        }

        console.log('Session:', session);

        if (session?.user) {
          // Get the pending beta code from localStorage
          const pendingBetaCode = localStorage.getItem('pendingBetaCode');
          console.log('Pending beta code:', pendingBetaCode);
          
          // Create or update the user's profile
          const profileData: ProfileInsert = {
            id: session.user.id,
            full_name: session.user.user_metadata.full_name || null,
            avatar_url: session.user.user_metadata.avatar_url || null,
            beta_code: pendingBetaCode || null,
            created_at: new Date().toISOString()
          };

          console.log('Profile data to insert:', profileData);

          // First check if profile exists
          const { data: existingProfile, error: checkError } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', session.user.id)
            .single();

          console.log('Existing profile check:', { existingProfile, checkError });

          if (checkError && checkError.code !== 'PGRST116') { // PGRST116 is "not found"
            console.error('Error checking existing profile:', checkError);
            throw checkError;
          }

          // If profile doesn't exist, insert it
          if (!existingProfile) {
            console.log('No existing profile found, inserting new profile...');
            const { error: insertError } = await supabase
              .from('profiles')
              .insert(profileData);

            if (insertError) {
              console.error('Error inserting profile:', insertError);
              throw insertError;
            }
            console.log('Profile inserted successfully');
          } else {
            console.log('Profile already exists, updating...');
            // Only update beta_code if we have a pending one
            const updateData = pendingBetaCode 
              ? profileData 
              : {
                  full_name: profileData.full_name,
                  avatar_url: profileData.avatar_url,
                  created_at: profileData.created_at
                };

            const { error: updateError } = await supabase
              .from('profiles')
              .update(updateData)
              .eq('id', session.user.id);

            if (updateError) {
              console.error('Error updating profile:', updateError);
              throw updateError;
            }
            console.log('Profile updated successfully');
          }

          // Clear the pending beta code from localStorage
          localStorage.removeItem('pendingBetaCode');

          // Navigate to home page
          navigate('/', { replace: true });
          toast.success('Successfully signed in!');
        } else {
          console.error('No session found');
          throw new Error('No session found');
        }
      } catch (error) {
        console.error('Error in auth callback:', error);
        toast.error('An error occurred during signup. Please try again.');
        navigate('/auth/signin', { replace: true });
      }
    };

    handleAuthCallback();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="flex items-center space-x-2">
        <Loader2 className="h-6 w-6 animate-spin" />
        <span>Completing signup...</span>
      </div>
    </div>
  );
} 